import React, { FormEvent, useEffect, useState } from 'react'
import { navigate } from 'gatsby'
import { Helmet } from 'react-helmet'
import { Button } from '@/components/atoms/Button/Button'
import { renderFormField } from '@/helpers/formFieldRenderer'
import { hubspotFormSubmission } from '@/helpers/hubspotFormSubmission'
import { removeFoldersFromUrl } from '@/helpers/removeFoldersFromUrl'
import { EventFormProps, FormData as FormDataTypes } from './types'

declare global {
  interface Window {
    turnstile: any
  }
}

const SITE_KEY = process.env.GATSBY_CLOUDFLARE_CAPTCHA_SITE_KEY

export function EventForm({ fields, button, hubspot_form_id, hubspot_portal_id, redirect_url }: EventFormProps) {
  const [formData, setFormData] = useState<FormDataTypes>({
    name: '',
    surname: '',
    email: '',
    company: '',
    role: null,
    gdpr: false,
    subscription: false
  })

  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)

  const formRedirect = redirect_url?.url || removeFoldersFromUrl(redirect_url?.cached_url)

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (window.turnstile && document.getElementById('cf-turnstile')) {
        clearInterval(intervalId)
        window.turnstile.render('#cf-turnstile', {
          sitekey: SITE_KEY,
          callback: (token: string) => {
            setCaptchaToken(token)
            setErrorMessage('')
          }
        })
      }
    }, 100)

    return () => clearInterval(intervalId)
  }, [])

  const onChangeHandler = (name: string, value: unknown): void => {
    setFormData({ ...formData, [name]: value })
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()

    if (!captchaToken) {
      setErrorMessage('CAPTCHA validation failed. Please try again.')
      return
    }

    const communications = fields.some((field) => field.name === 'subscription') && [
      {
        value: String(formData.subscription),
        subscriptionTypeId: 443696569,
        text: "I’d like to subscribe to 'The Deliverable' newsletter. I understand I can unsubscribe at any time"
      }
    ]
    const legalConsentOptions = fields.some((field) => field.name === 'gdpr') && {
      consent: {
        consentToProcess: String(formData.gdpr),
        text: 'I agree to receive communications from Beebole regarding this event and others',
        communications
      }
    }
    hubspotFormSubmission(
      hubspot_portal_id,
      hubspot_form_id,
      [
        {
          name: 'firstname',
          value: formData.name
        },
        {
          name: 'lastname',
          value: formData.surname
        },
        {
          name: 'company',
          value: formData.company
        },
        {
          name: 'email',
          value: formData.email
        },
        {
          name: 'jobtitle',
          value: formData.role?.value as string
        }
      ],
      legalConsentOptions,
      setSuccessMessage,
      setErrorMessage
    )

    if (!errorMessage && redirect_url) {
      navigate(formRedirect)
    }
  }

  return (
    <>
      <Helmet>
        <script src="https://challenges.cloudflare.com/turnstile/v0/api.js" async defer />
      </Helmet>
      <form
        className="w-full bg-white shadow-form rounded p-24 tablet:py-24 tablet:px-32 netbook:py-32 netbook:px-64"
        onSubmit={handleSubmit}
      >
        {fields?.map((field) => (
          <div key={field._uid}>
            <label htmlFor={field.name} className="text-bodyL font-bold text-text-dark-900 mb-8">
              {field.label}
            </label>
            {renderFormField({
              component: field.component,
              field,
              // @ts-ignore
              value: field.component === 'component_checkbox' ? Boolean(formData[field.name]) : formData[field.name],
              onChange: onChangeHandler
            })}
          </div>
        ))}
        {button.length > 0 ? (
          <Button {...button[0]} className="w-full py-16" type="submit">
            {button[0].text}
          </Button>
        ) : null}
        {successMessage || errorMessage ? (
          <div
            dangerouslySetInnerHTML={{ __html: successMessage || errorMessage }}
            className="text-bodyM text-center mt-20"
          />
        ) : null}
        <div id="cf-turnstile" className="mt-20 flex justify-center" />
      </form>
    </>
  )
}
